import React, { useState, useEffect, useRef } from 'react';
import {
  getBackupSettings, updateBackupSettings, triggerBackup,
  listBackups, downloadBackup, deleteBackup, restoreBackup
} from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import {
  Database, Download, Upload, Trash2, Play, Clock, Settings as SettingsIcon,
  CheckCircle2, AlertCircle, HardDrive, Calendar, FileArchive,
  RefreshCw, AlertTriangle
} from 'lucide-react';
import { Lock, Cloud } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import Pagination from '../components/Pagination';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from '../components/ui/dialog';
import { toast } from 'sonner';

const formatBytes = (bytes) => {
  if (!bytes) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
};

const formatDate = (d) => {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleDateString('pt-BR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  } catch { return d; }
};

const STATUS_BADGE = {
  success: { label: 'Sucesso', icon: CheckCircle2, className: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  failed: { label: 'Falhou', icon: AlertCircle, className: 'bg-red-50 text-red-700 border-red-200' },
  in_progress: { label: 'Em andamento', icon: Clock, className: 'bg-amber-50 text-amber-700 border-amber-200' },
  pruned: { label: 'Removido (retenção)', icon: HardDrive, className: 'bg-gray-100 text-gray-600 border-gray-200' },
};

const Backups = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [settings, setSettings] = useState(null);
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);
  const [creating, setCreating] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [restoreOpen, setRestoreOpen] = useState(false);
  const [restoreFile, setRestoreFile] = useState(null);
  const [restoring, setRestoring] = useState(false);
  const [backupsPage, setBackupsPage] = useState(1);
  const BACKUPS_PAGE_SIZE = 10;
  const fileInputRef = useRef(null);

  useEffect(() => { setBackupsPage(1); }, [backups.length]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [s, b] = await Promise.all([getBackupSettings(), listBackups()]);
      setSettings(s.data);
      setBackups(b.data);
    } catch (e) {
      toast.error('Erro ao carregar configurações de backup');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleSettingChange = async (changes) => {
    setSavingSettings(true);
    try {
      const res = await updateBackupSettings(changes);
      setSettings(res.data);
      toast.success('Configurações atualizadas');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao atualizar');
    } finally {
      setSavingSettings(false);
    }
  };

  const handleTrigger = async () => {
    setCreating(true);
    try {
      await triggerBackup();
      toast.success('Backup criado com sucesso');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao criar backup');
    } finally {
      setCreating(false);
    }
  };

  const handleDownload = async (b) => {
    try {
      const res = await downloadBackup(b.id);
      const blob = new Blob([res.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', b.filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Download iniciado');
    } catch (e) {
      toast.error('Erro ao baixar backup');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteBackup(deleteTarget.id);
      toast.success('Backup removido');
      setDeleteTarget(null);
      fetchData();
    } catch (e) {
      toast.error('Erro ao remover');
    }
  };

  const handleRestore = async () => {
    if (!restoreFile) return;
    setRestoring(true);
    try {
      const res = await restoreBackup(restoreFile);
      const s = res.data.summary || {};
      const total = s.total_documents ?? 0;
      const usersN = s.collections_restored?.users ?? 0;
      const licN = s.collections_restored?.licenses ?? 0;
      if (total === 0) {
        toast.warning('Nenhum registro foi importado (backup vazio). Dados atuais preservados.', { duration: 8000 });
      } else {
        toast.success(`Restaurado: ${total} registros (${usersN} usuários, ${licN} licenças) + ${s.uploads_restored ?? 0} arquivos`, { duration: 6000 });
      }
      if (s.skipped_empty?.length > 0) {
        toast.info(`Collections preservadas (vinham vazias no backup): ${s.skipped_empty.join(', ')}`, { duration: 8000 });
      }
      setRestoreOpen(false);
      setRestoreFile(null);
      setTimeout(() => window.location.reload(), 2500);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao restaurar');
    } finally {
      setRestoring(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  const totalSize = backups.filter(b => b.status === 'success').reduce((acc, b) => acc + (b.size_bytes || 0), 0);
  const successCount = backups.filter(b => b.status === 'success').length;
  const prunedCount = backups.filter(b => b.status === 'pruned').length;

  const pagedBackups = backups.slice((backupsPage - 1) * BACKUPS_PAGE_SIZE, backupsPage * BACKUPS_PAGE_SIZE);

  return (
    <div className="space-y-6" data-testid="backups-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
            <Database className="w-7 h-7 text-[#003366]" />
            Backups
          </h1>
          <p className="text-gray-500 mt-1">Banco de dados e arquivos anexados do sistema</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => setRestoreOpen(true)}
            className="bg-white border-gray-300 text-gray-700 hover:bg-gray-50 font-semibold"
            data-testid="open-restore-btn"
            disabled={!isAdmin}
          >
            <Upload className="w-4 h-4 mr-2" />
            Restaurar
          </Button>
          <Button
            onClick={handleTrigger}
            disabled={creating}
            className="bg-[#003366] hover:bg-[#002244] font-semibold"
            data-testid="trigger-backup-btn"
          >
            <Play className="w-4 h-4 mr-2" />
            {creating ? 'Gerando...' : 'Backup agora'}
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Backups disponíveis', value: successCount, icon: FileArchive, color: 'text-[#003366]', bg: 'bg-blue-50', hint: prunedCount > 0 ? `+ ${prunedCount} no histórico (removidos)` : null },
          { label: 'Espaço utilizado', value: formatBytes(totalSize), icon: HardDrive, color: 'text-emerald-600', bg: 'bg-emerald-50' },
          { label: 'Agendamento', value: settings?.auto_enabled ? settings.schedule_time : 'Desligado', icon: Clock, color: 'text-amber-600', bg: 'bg-amber-50' },
          { label: 'Último auto', value: settings?.last_auto_run ? formatDate(settings.last_auto_run).split(' ')[0] : '—', icon: Calendar, color: 'text-gray-600', bg: 'bg-gray-100' },
        ].map((s) => (
          <Card key={s.label} className="bg-white border-gray-200 shadow-sm">
            <CardContent className="p-4 flex items-center gap-3">
              <div className={`p-2.5 rounded-lg ${s.bg}`}><s.icon className={`w-5 h-5 ${s.color}`} /></div>
              <div className="min-w-0">
                <p className="text-xl font-semibold text-gray-900 truncate">{s.value}</p>
                <p className="text-xs text-gray-500">{s.label}</p>
                {s.hint && <p className="text-[10px] text-gray-400 mt-0.5">{s.hint}</p>}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Settings */}
      <Card className="bg-white border-gray-200 shadow-sm">
        <CardHeader className="border-b border-gray-100">
          <CardTitle className="text-lg flex items-center gap-2 text-gray-900 font-semibold">
            <SettingsIcon className="w-5 h-5 text-[#003366]" />
            Configurações
          </CardTitle>
        </CardHeader>
        <CardContent className="p-5 space-y-5">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <p className="font-semibold text-gray-900">Backup automático diário</p>
              <p className="text-sm text-gray-500">Roda no horário configurado, todos os dias</p>
            </div>
            <Switch
              checked={settings?.auto_enabled || false}
              onCheckedChange={(v) => handleSettingChange({ auto_enabled: v })}
              disabled={savingSettings}
              data-testid="auto-enabled-switch"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-gray-700 font-semibold">Horário do backup automático</Label>
              <Input
                type="time"
                value={settings?.schedule_time || '02:00'}
                onChange={(e) => setSettings({ ...settings, schedule_time: e.target.value })}
                onBlur={() => handleSettingChange({ schedule_time: settings.schedule_time })}
                className="bg-white border-gray-300 text-gray-900 w-40"
                disabled={!settings?.auto_enabled || savingSettings}
                data-testid="schedule-time-input"
              />
              <p className="text-xs text-gray-500">Fuso horário: America/Sao_Paulo</p>
            </div>
            <div className="space-y-2">
              <Label className="text-gray-700 font-semibold">Retenção (manter últimos N backups)</Label>
              <Input
                type="number"
                min="0"
                value={settings?.retention_count ?? 30}
                onChange={(e) => setSettings({ ...settings, retention_count: parseInt(e.target.value || 0) })}
                onBlur={() => handleSettingChange({ retention_count: settings.retention_count })}
                className="bg-white border-gray-300 text-gray-900 w-40"
                disabled={savingSettings}
                data-testid="retention-count-input"
              />
              <p className="text-xs text-gray-500">0 = ilimitado (manter todos)</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* History */}
      <Card className="bg-white border-gray-200 shadow-md">
        <CardHeader className="border-b border-gray-100 flex flex-row items-center justify-between space-y-0">
          <CardTitle className="text-lg flex items-center gap-2 text-gray-900 font-semibold">
            <FileArchive className="w-5 h-5 text-[#003366]" />
            Histórico de Backups
          </CardTitle>
          <Button variant="ghost" size="sm" onClick={fetchData} className="text-gray-500 hover:text-gray-900" data-testid="refresh-backups-btn">
            <RefreshCw className="w-4 h-4 mr-1" />
            Atualizar
          </Button>
        </CardHeader>
        <CardContent className="p-0">
          {backups.length === 0 ? (
            <div className="text-center py-16 text-gray-400">
              <FileArchive className="w-14 h-14 mx-auto mb-3 opacity-40" />
              <p className="font-medium">Nenhum backup ainda</p>
              <p className="text-sm mt-1">Clique em &ldquo;Backup agora&rdquo; para criar o primeiro</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {pagedBackups.map((b) => {
                const cfg = STATUS_BADGE[b.status] || STATUS_BADGE.in_progress;
                const Icon = cfg.icon;
                return (
                  <div
                    key={b.id}
                    className="flex items-center justify-between px-5 py-3 hover:bg-gray-50 transition-colors gap-3"
                    data-testid={`backup-row-${b.id}`}
                  >
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <div className={`p-2 rounded-lg ${cfg.className.split(' ')[0]}`}>
                        <Icon className={`w-4 h-4 ${cfg.className.split(' ')[1]}`} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-mono text-sm font-medium text-gray-900 truncate">{b.filename}</span>
                          {(b.encrypted || b.filename?.endsWith('.enc')) && (
                            <Badge variant="outline" className="bg-slate-100 text-slate-700 border-slate-200 text-xs" data-testid={`backup-encrypted-${b.id}`}>
                              <Lock className="w-3 h-3 mr-1" /> Criptografado
                            </Badge>
                          )}
                          <Badge variant="outline" className={cfg.className + ' text-xs'}>{cfg.label}</Badge>
                          <Badge variant="outline" className={
                            b.type === 'auto'
                              ? 'bg-blue-50 text-blue-700 border-blue-200 text-xs'
                              : 'bg-purple-50 text-purple-700 border-purple-200 text-xs'
                          }>
                            {b.type === 'auto' ? 'Automático' : 'Manual'}
                          </Badge>
                          {b.status === 'success' && b.s3_uploaded && (
                            <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-xs" data-testid={`backup-s3-${b.id}`}>
                              <Cloud className="w-3 h-3 mr-1" /> Enviado ao S3
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                          <span>{formatDate(b.created_at)}</span>
                          <span>•</span>
                          <span>{formatBytes(b.size_bytes)}</span>
                          {b.uploads_count > 0 && (<><span>•</span><span>{b.uploads_count} arquivo(s)</span></>)}
                          {b.created_by_name && (<><span>•</span><span>por {b.created_by_name}</span></>)}
                        </div>
                        {b.error && <p className="text-xs text-red-600 mt-1">Erro: {b.error}</p>}
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      {b.status === 'success' && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDownload(b)}
                          className="text-gray-500 hover:text-[#003366] h-8 w-8"
                          data-testid={`download-${b.id}`}
                          title="Baixar"
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setDeleteTarget(b)}
                        className="text-gray-500 hover:text-red-600 h-8 w-8"
                        data-testid={`delete-${b.id}`}
                        title="Remover"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          <Pagination page={backupsPage} pageSize={BACKUPS_PAGE_SIZE} total={backups.length} onPageChange={setBackupsPage} testId="backups-pagination" />
        </CardContent>
      </Card>

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent className="bg-white border-gray-200">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-gray-900">Remover backup?</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-600">
              O arquivo <span className="font-mono font-semibold">{deleteTarget?.filename}</span> será excluído permanentemente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-white border-gray-300 text-gray-700">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700 text-white" data-testid="confirm-delete-btn">
              Remover
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Restore Dialog */}
      <Dialog open={restoreOpen} onOpenChange={(o) => { setRestoreOpen(o); if (!o) setRestoreFile(null); }}>
        <DialogContent className="bg-white border-gray-200 sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-gray-900 flex items-center gap-2">
              <Upload className="w-5 h-5 text-[#003366]" />
              Restaurar Backup
            </DialogTitle>
            <DialogDescription className="text-gray-500">
              Faça upload de um arquivo de backup (.zip.enc criptografado ou .zip legado) para restaurar
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="rounded-md bg-amber-50 border border-amber-200 p-3 flex gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-700 flex-shrink-0 mt-0.5" />
              <div className="text-xs text-amber-800">
                <strong>Atenção:</strong> Esta ação irá <strong>substituir todos os dados atuais</strong> (licenças, usuários, chamados e arquivos anexados) pelos dados do backup. Não pode ser desfeita.
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-gray-700 font-semibold">Arquivo de backup (.zip.enc ou .zip)</Label>
              <input
                ref={fileInputRef}
                type="file"
                accept=".zip,.enc"
                onChange={(e) => setRestoreFile(e.target.files?.[0] || null)}
                className="block w-full text-sm text-gray-700 file:mr-3 file:py-2 file:px-3 file:rounded file:border-0 file:bg-[#003366] file:text-white file:font-semibold hover:file:bg-[#002244]"
                data-testid="restore-file-input"
              />
              {restoreFile && (
                <p className="text-xs text-gray-600">
                  Selecionado: <span className="font-mono">{restoreFile.name}</span> ({formatBytes(restoreFile.size)})
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setRestoreOpen(false); setRestoreFile(null); }} className="bg-white border-gray-300 text-gray-700">
              Cancelar
            </Button>
            <Button
              onClick={handleRestore}
              disabled={!restoreFile || restoring}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-restore-btn"
            >
              {restoring ? 'Restaurando...' : 'Confirmar restauração'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Backups;
